import * as i18n from 'i18next';
import { ITranslateProps } from 'models/common';
import * as moment from 'moment';
import { momentWithTimezone } from 'redux/modules/shop/helpers';

// a simple memoize function that takes in a function
// and returns a memoized function
export const memoizeFunction = (fn: (value: any) => void, argumentIndexToCheck?: number) => {
	const cache = {};
	return (...args) => {
		const n = args[argumentIndexToCheck ? argumentIndexToCheck : 0]; // just taking one argument here
		if (n in cache) {
			return cache[n];
		} else {
			const result = fn(n);
			cache[n] = result;
			return result;
		}
	};
};

export const alphaNumeric = (value) =>
	value && /[^a-zA-Z0-9 ]/i.test(value) ? i18n.t('Validation_Client_Field_AlphaNumeric') : undefined;
export const email = (value) =>
	value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
		? i18n.t('Validation_Client_Field_Email')
		: undefined;
export const maxLength = (max) => (value) =>
	value && value.length > max ? i18n.t('Validation_Client_Field_MaxLength', { maxLength: max }) : undefined;
export const minLength = (min) => (value) =>
	value && value.length < min ? i18n.t('Validation_Client_Field_MinLength', { minLength: min }) : undefined;
export const isNumber = (value) =>
	value && Number.isNaN(Number(value)) ? i18n.t('Validation_Client_Field_Number') : undefined;
export const isWholeNumber = (value) =>
	value && !Number.isInteger(Number(value)) ? i18n.t('Validation_Client_Field_WholeNumber') : undefined;
export const minValue = (min) => (value) =>
	value && value < min ? i18n.t('Validation_Client_Field_MinValue', { min }) : undefined;
export const maxValue = (max) => (value) =>
	value && value > max ? i18n.t('Validation_Client_Field_MaxValue', { max }) : undefined;
export const required = (value) => (value ? undefined : i18n.t('Validation_Client_Field_Required'));
export const multiCheckboxRequired = (value) =>
	value === undefined || value === null || value.length === 0 ? i18n.t('Validation_Client_Field_Required') : undefined;
export const dateRequired = (value) => (!moment(value).isValid ? i18n.t('Validation_Client_Field_Date') : undefined);
export const multiCheckboxMaxLength = (max) => (value) =>
	value && value.length > max ? `You can select max ${max} items` : undefined;
export const nonWhitespace = (value) =>
	value && !/^\S*$/i.test(value) ? i18n.t('Validation_Client_Field_NonWhitespace') : undefined;
export const phoneNumber = (value) =>
	value && !/^\d+(?:\s?\d+)*$/.test(value) ? i18n.t('Validation_Client_Field_PhoneNumber') : undefined;
export const requiredNumber = (value) => (value != undefined ? undefined : i18n.t('Validation_Client_Field_Required'));

//Used for Service group validation on ShopServicesAddEditForm component
export const serviceGroup = (value: string, allValues: any, props: any) => {
	const { serviceGroups } = props;
	const currentCategory = allValues.info.serviceCategory;
	const currentServiceGroup = serviceGroups.find((item) => item.id === value);
	return currentCategory === currentServiceGroup?.serviceCategoryId
		? undefined
		: i18n.t('Validation_Client_Field_Required');
};

/* Friendly Url Field */
export const alphabetAndMinusCharacter = (value) =>
	value && /[^a-zA-Z0-9-]/i.test(value) ? i18n.t('Validation_Client_Field_AlphaNumericAndMinusCharacter') : undefined;
/* Friendly Url Field */

type ValidationFunction = (value: string) => string | undefined;
const timeValidationFnCache: { [key: string]: ValidationFunction } = {};
export const createEndTimeValidationFuntion = (
	startTime: string | Date,
	t: ITranslateProps['t'],
	timezone: string,
	allowEqual: boolean = false,
): ValidationFunction => {
	const startMoment = momentWithTimezone(timezone, startTime);
	const minTime = startMoment.unix() - startMoment.startOf('day').unix();
	const key = `${minTime}_${allowEqual}`;
	if (!(key in timeValidationFnCache)) {
		timeValidationFnCache[key] = (value) => {
			if (!value) {
				return undefined;
			}
			const valMoment = momentWithTimezone(timezone, value);
			const val = valMoment.unix() - valMoment.startOf('day').unix();
			if (val < minTime || (!allowEqual && val == minTime)) {
				let min = momentWithTimezone(timezone, startTime) as any;
				if (!allowEqual) {
					min = min.add(5, 'minutes');
				}
				min = min.format('HH:mm');
				return t('Validation_Client_Field_MinValue', { min });
			}
			return undefined;
		};
	}
	return timeValidationFnCache[key];
};

// Normalize functions
export const aboveZero = (value: number) => {
	if (value < 0) {
		return 0;
	} else {
		return value;
	}
};

// For price fields
export const priceFieldNormalization = (value) => {
	value = value.replace(',', '.');
	value = value.replace(/[^0-9.]/g, ''); // Remove all chars except numbers and "."

	// Create an array with sections split by "."
	const sections = value.split('.');

	// If numbers exist after first .
	if (sections[1]) {
		return sections[0] + '.' + sections[1].slice(0, 2); // Join first two sections and truncate end section to length 2
	} else {
		return value; // Else return value (needs to return value instead of section[0] otherwise we cannot add ".")
	}
};

// For discount fields
export const discountFieldNormalization = (value) => {
	value = value.replace(',', '.');
	value = value.replace(/[^0-9.]/g, ''); // Remove all chars except numbers and "."

	// Create an array with sections split by "."
	const sections = value.split('.');

	// If numbers exist after first .
	if (sections[1]) {
		return sections[0] + '.' + sections[1].slice(0, 2); // Join first two sections and truncate end section to length 2
	} else {
		return value; // Else return value (needs to return value instead of section[0] otherwise we cannot add ".")
	}
};

// Pass this into reduxForm({onSubmitFail})
// if header, elements need to have scroll-margin-top
export const scrollErrorIntoView = (className = '.withError', timeout = 500) => {
	setTimeout(() => {
		document.querySelector(className)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, timeout);
};
